/**
 * Komprese videa v prohlížeči přes FFmpeg.wasm (single-threaded, bez SharedArrayBuffer).
 * WASM core (~30 MB) se stáhne z CDN jen jednou při prvním nahrávání videa.
 *
 * Výstup: MP4 H.264, max 1280 px šířka, CRF 23, AAC 128 kbps.
 * Typická komprese: 1 GB → 100–200 MB (závisí na obsahu).
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

const CORE_CDN = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm';
const MIN_SIZE_TO_COMPRESS = 5 * 1024 * 1024; // soubory < 5 MB komprimovat nestojí za to

let ffmpegInstance = null;
let ffmpegLoadPromise = null;
let activeProgressCb = null; // platí jen po dobu jednoho běhu compressVideo

async function getFFmpeg() {
    if (ffmpegInstance) return ffmpegInstance;
    if (ffmpegLoadPromise) return ffmpegLoadPromise;
    ffmpegLoadPromise = (async () => {
        const f = new FFmpeg();
        // Listener registrujeme JEDNOU — FFmpeg.on() přidává callback do pole bez
        // automatického odebrání, takže opakovaná registrace by je hromadila.
        f.on('progress', ({ progress }) => {
            activeProgressCb?.(Math.min(99, Math.max(0, Math.round(progress * 100))));
        });
        await f.load({
            coreURL: await toBlobURL(`${CORE_CDN}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${CORE_CDN}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        ffmpegInstance = f;
        return f;
    })();
    // Po neúspěšném načtení (např. nedostupné CDN) povol další pokus místo
    // zaseknutí na trvale odmítnutém promise.
    ffmpegLoadPromise.catch(() => { ffmpegLoadPromise = null; });
    return ffmpegLoadPromise;
}

/** Bude se tento soubor komprimovat? (pro zobrazení hlášky před spuštěním) */
export function willCompressVideo(file) {
    return !!file && file.type.startsWith('video/') && file.size >= MIN_SIZE_TO_COMPRESS;
}

/**
 * Zkomprimuje video soubor.
 * @param {File} file
 * @param {{ maxWidth?: number, onProgress?: (pct: number) => void }} options
 * @returns {Promise<File>} zkomprimovaný soubor nebo originál při chybě / malém souboru
 */
export async function compressVideo(file, { maxWidth = 1280, onProgress } = {}) {
    if (!willCompressVideo(file)) return file;

    try {
        const f = await getFFmpeg();

        const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
        const inName  = `input.${ext}`;
        const outName = 'output.mp4';

        activeProgressCb = onProgress || null;

        await f.writeFile(inName, await fetchFile(file));
        await f.exec([
            '-i', inName,
            // šířka = min(vstup, maxWidth) → bez zvětšování; výška = -2 → dopočítá
            // se podle poměru stran a zaokrouhlí na sudou (libx264 vyžaduje sudé rozměry)
            '-vf', `scale='min(iw,${maxWidth})':-2`,
            '-c:v', 'libx264',
            '-crf', '23',
            '-preset', 'fast',
            '-c:a', 'aac',
            '-b:a', '128k',
            '-movflags', '+faststart',
            outName
        ]);

        const data = await f.readFile(outName);
        await f.deleteFile(inName).catch(() => {});
        await f.deleteFile(outName).catch(() => {});

        const outFileName = file.name.replace(/\.[^.]+$/, '') + '.mp4';
        return new File([data], outFileName, { type: 'video/mp4' });
    } catch (err) {
        console.warn('Video komprese selhala, nahrávám originál:', err);
        return file;
    } finally {
        activeProgressCb = null;
    }
}
